import { VSF_MHJ_MY_STORE_ID, VSF_MHJ_MY_STORE_NAME, VSF_MHJ_MY_STORE_SEARCH_TERM } from '~/config/cookie'

export type AuthCookies = {
  accessToken: string
  refreshToken: string
  expiresIn: number
  refreshTokenExpiresIn: number
}

type Cookies =
  | 'access_token'
  | 'refresh_token'
  | typeof VSF_MHJ_MY_STORE_SEARCH_TERM
  | typeof VSF_MHJ_MY_STORE_ID
  | typeof VSF_MHJ_MY_STORE_NAME

export const setAuthCookies = ({ accessToken, refreshToken, expiresIn, refreshTokenExpiresIn }: AuthCookies) => {
  const accessTokenCookie = useCookie('access_token', {
    maxAge: expiresIn, // SLAS access tokens: 30 minutes
    secure: true,
    sameSite: 'lax',
    path: '/',
  })
  accessTokenCookie.value = accessToken

  // SLAS public client refresh tokens are single-use (rotated on each use).
  // Token-validity: 90 days (registered shopper), 30 days (guest), 9 days (non-prod).
  // https://developer.salesforce.com/docs/commerce/commerce-api/guide/slas.html#access-tokens-and-refresh-tokens
  const refreshTokenCookie = useCookie('refresh_token', {
    maxAge: refreshTokenExpiresIn,
    secure: true,
    sameSite: 'lax',
    path: '/',
  })
  refreshTokenCookie.value = refreshToken
}

// `await` on a non-thenable resolves rather than throws, so try/catch can't catch a malformed SDK
// token response. Expiries are used directly as cookie maxAge, so a blank token or non-numeric expiry
// must fail here too — otherwise it yields an unusable cookie and a half-authenticated session.
export function isValidTokenResponse<T>(
  value: T,
): value is T & { access_token: string; refresh_token: string; expires_in: number; refresh_token_expires_in: number } {
  const isPositiveFiniteNumber = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as {
    access_token?: unknown
    refresh_token?: unknown
    expires_in?: unknown
    refresh_token_expires_in?: unknown
  }
  return (
    typeof candidate.access_token === 'string' &&
    candidate.access_token !== '' &&
    typeof candidate.refresh_token === 'string' &&
    candidate.refresh_token !== '' &&
    isPositiveFiniteNumber(candidate.expires_in) &&
    isPositiveFiniteNumber(candidate.refresh_token_expires_in)
  )
}

export const clearCookies = (names: Cookies[]) =>
  names.forEach((name) => {
    const cookie = useCookie(name, {
      path: '/',
    })
    cookie.value = null
    refreshCookie(name)
  })

type JwtPayload = {
  exp: number
  sub?: string
  isb?: string
  [key: string]: unknown
}

const parseJwtPayload = (token: string): JwtPayload => {
  const parts = token.split('.')
  if (parts.length !== 3 || !parts[1]) {
    throw new Error('Invalid JWT format')
  }
  const payload: unknown = JSON.parse(atob(parts[1]))
  if (
    typeof payload !== 'object' ||
    payload === null ||
    !('exp' in payload) ||
    typeof (payload as JwtPayload).exp !== 'number'
  ) {
    throw new Error('JWT payload missing or invalid exp claim')
  }
  return payload as JwtPayload
}

export const isTokenExpired = (token: string): boolean => {
  try {
    const { exp } = parseJwtPayload(token)
    const currentTime = Math.floor(Date.now() / 1000)
    return currentTime >= exp - 5 * 60
  } catch (e) {
    console.error('Error occurred while parsing token:', e)
    return true
  }
}

export const isTokenValid = (): boolean => {
  const token = useCookie('access_token').value
  return Boolean(token && !isTokenExpired(token))
}

export const decodeToken = (token: string | null) => {
  if (!token) {
    throw new Error('No user token provided')
  }

  const parts = token.split('.')
  if (parts.length !== 3 || !parts[0] || !parts[2]) {
    throw new Error('Invalid token format')
  }

  const header: unknown = JSON.parse(atob(parts[0]))
  const payload = parseJwtPayload(token)

  return {
    header,
    payload,
    signature: parts[2],
  }
}

export const isGuestToken = (token: string): boolean => {
  try {
    const tokenPayload = decodeToken(token).payload
    const isbField: string | undefined = tokenPayload?.isb
    if (!isbField) {
      throw new Error('Invalid user token')
    }
    const isbParts = isbField.split('::')
    const upnPart = isbParts.find((part) => part.startsWith('upn:'))
    return upnPart?.split(':')[1]?.toLowerCase() === 'guest'
  } catch (e) {
    console.error('Error occurred while parsing token:', e)
    return false
  }
}

export const isRegisteredToken = (token: string | null | undefined): boolean => (token ? !isGuestToken(token) : false)

export const getIdsFromToken = (token: string | null) => {
  if (!token) {
    throw new Error('No user token provided')
  }
  const tokenPayload = decodeToken(token).payload
  const isbField: string | undefined = tokenPayload.isb
  const subField: string | undefined = tokenPayload.sub

  if (!isbField) {
    throw new Error('Invalid user token')
  }

  const isbParts = isbField.split('::')

  const rcidPart = isbParts.find((part) => part.startsWith('rcid:'))

  const gcidPart = isbParts.find((part) => part.startsWith('gcid:'))

  const subParts = subField?.split('::')

  const usid = subParts?.find((part) => part.startsWith('usid:'))

  return { customerId: rcidPart?.split(':')[1] || gcidPart?.split(':')[1], usId: usid?.split(':')[1] }
}
